import React, { useEffect, useState } from 'react';
import { useAuth } from './context/AuthContext';
import { supabase } from './lib/supabaseClient';
import { writeSessionHandoff } from './utils/sessionHandoff';
import { resolveScraperOrigin } from './utils/scraperUrl';
import ThemeToggle from './components/ThemeToggle';
import Logo from './components/brand/Logo';

// LoginGate screens, all rendered in the same card:
//   'login'   – email + password (Supabase sign-in)
//   'signup'  – email + username + password + confirm (Supabase sign-up)
//   'forgot'  – email-only form; Supabase emails a password-reset link
//   'reset'   – set a new password (shown after the user follows that link)
// Auth is delegated to Supabase (shared with the marketplace app); the local
// backend only verifies the token and tracks role + approval.
function LoginGate({ children }) {
  const {
    isAuthenticated,
    pendingApproval,
    recoveryMode,
    recoveryFailed,
    login,
    signup,
    requestPasswordReset,
    updatePassword,
    logout,
    loading,
    error,
  } = useAuth();

  const [redirectingToNext, setRedirectingToNext] = useState(false);
  // Set when we have a ?next= target but no live session to hand it. Without
  // it the render below would sit on a blank screen forever waiting for a
  // redirect that is never coming.
  const [handoffFailed, setHandoffFailed] = useState(false);
  const scraperOrigin = React.useMemo(
    () => resolveScraperOrigin(process.env.REACT_APP_SCRAPER_ORIGIN),
    [],
  );

  const getSafeNextUrl = React.useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const rawNext = params.get('next');
    if (!rawNext) return null;

    try {
      const target = new URL(rawNext, window.location.origin);
      const allowedOrigins = new Set([window.location.origin, scraperOrigin]);
      if (!allowedOrigins.has(target.origin)) {
        return null;
      }
      return target;
    } catch {
      return null;
    }
  }, [scraperOrigin]);

  const redirectToNextIfPresent = React.useCallback(async () => {
    const target = getSafeNextUrl();
    if (!target) return false;

    if (target.origin === scraperOrigin) {
      // Ask Supabase for the session rather than reading the copy mirrored into
      // localStorage: getSession() refreshes an access token that has expired or
      // is about to, so the scraper never receives a dead one. Handing over the
      // stored copy is what produced the auction.ai <-> scraper redirect loop —
      // this page marks itself authenticated synchronously from localStorage, so
      // it could redirect with a stale token before Supabase had refreshed it.
      let session = null;
      try {
        const { data } = await supabase.auth.getSession();
        session = data?.session || null;
      } catch (_e) {
        session = null;
      }

      if (!session?.access_token) {
        // No live session to pass on. Fall through to the login form instead of
        // sending the other app a credential it is certain to reject.
        setHandoffFailed(true);
        setRedirectingToNext(false);
        return false;
      }

      // The session goes in a short-lived cookie on the shared parent domain,
      // never in the URL. That keeps the credential out of history and access
      // logs, and lets the refresh token travel too, so the scraper ends up with
      // a session it can renew itself instead of one that dies within the hour.
      let parsedUser = null;
      try {
        const stored = localStorage.getItem('auth_user');
        parsedUser = stored ? JSON.parse(stored) : null;
      } catch (_e) {
        parsedUser = null;
      }
      writeSessionHandoff(session, parsedUser);
    }

    setRedirectingToNext(true);
    window.location.replace(target.toString());
    return true;
  }, [getSafeNextUrl, scraperOrigin]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('logout') === 'true') {
      logout();
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [logout]);

  useEffect(() => {
    // During password recovery Supabase has established a (temporary) session,
    // so isAuthenticated is true — but we must stay on the reset form rather
    // than bouncing to ?next=.
    if (!isAuthenticated || recoveryMode) return;
    void redirectToNextIfPresent();
  }, [isAuthenticated, recoveryMode, redirectToNextIfPresent]);

  // Seed from recoveryMode (itself seeded synchronously from the URL) so the
  // reset form renders on the first paint — no flash of the login fields.
  const [mode, setMode] = useState(recoveryMode ? 'reset' : 'login');

  // Safety net for recovery sessions detected asynchronously (event arrives
  // after the URL hash was already consumed): still jump to the reset form.
  useEffect(() => {
    if (recoveryMode) setMode('reset');
  }, [recoveryMode]);

  // A reset link that couldn't be verified: offer to send a new one.
  useEffect(() => {
    if (recoveryFailed) setMode('forgot');
  }, [recoveryFailed]);

  // Shared fields
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [localError, setLocalError] = useState('');
  const [notice, setNotice] = useState('');

  const clearMessages = () => {
    setLocalError('');
    setNotice('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    clearMessages();
    const ok = await login(email.trim().toLowerCase(), password);
    if (ok) {
      await redirectToNextIfPresent();
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    clearMessages();
    if (password !== confirmPassword) {
      setLocalError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters.');
      return;
    }
    const result = await signup({
      email: email.trim().toLowerCase(),
      username: username.trim(),
      password,
    });
    if (result && result.ok) {
      setMode('login');
      setPassword('');
      setConfirmPassword('');
      setNotice(
        'Account created. Confirm your email if prompted, then wait for an '
        + 'administrator to approve your account before logging in.'
      );
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    clearMessages();
    const result = await requestPasswordReset(email.trim().toLowerCase());
    if (result && result.ok) {
      setMode('login');
      setNotice(
        'If an account exists for that email, a password-reset link has been '
        + 'sent. Follow the link to choose a new password.'
      );
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    clearMessages();
    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setLocalError('Passwords do not match.');
      return;
    }
    const result = await updatePassword({ password });
    if (result && result.ok) {
      setMode('login');
      setPassword('');
      setConfirmPassword('');
      setNotice('Password updated. You can now log in with your new password.');
    } else {
      setLocalError(result?.error || 'Could not update password.');
    }
  };

  const switchMode = (next) => {
    setMode(next);
    clearMessages();
    setPassword('');
    setConfirmPassword('');
  };

  const hasNextTarget = Boolean(getSafeNextUrl());

  // A recovery session counts as authenticated, but we must keep rendering the
  // reset form so the user can choose a new password instead of being dropped
  // straight into the app (which silently spends the single-use recovery link).
  if (isAuthenticated && !recoveryMode) {
    if (redirectingToNext || (hasNextTarget && !handoffFailed)) {
      return null;
    }
    return children;
  }

  const displayError = localError || error;

  const titles = {
    login: 'Log in',
    signup: 'Create your account',
    forgot: 'Reset your password',
    reset: 'Choose a new password',
  };
  const submitLabels = {
    login: loading ? 'Signing in…' : 'Log In',
    signup: loading ? 'Creating account…' : 'Create account',
    forgot: loading ? 'Sending…' : 'Send reset link',
    reset: loading ? 'Updating…' : 'Set new password',
  };
  const handlers = {
    login: handleLogin,
    signup: handleSignup,
    forgot: handleForgot,
    reset: handleReset,
  };

  return (
    <div className="auth-aura relative min-h-screen flex items-center justify-center px-4 py-10">
      <ThemeToggle className="absolute top-4 right-4" />
      <div className="bg-surface/70 dark:bg-white/[0.04] backdrop-blur-xl border border-hairline rounded-2xl shadow-md p-8 w-full max-w-sm">
        <div className="flex justify-center mb-3">
          <Logo size="lg" />
        </div>
        <p className="text-center text-base font-medium text-muted mb-6">{titles[mode]}</p>

        {pendingApproval && (
          <p className="text-warn bg-warn/10 border border-warn/20 rounded p-3 text-sm mb-4">
            Your account is awaiting administrator approval.
          </p>
        )}

        <form onSubmit={handlers[mode]} className="space-y-4">
          {(mode === 'login' || mode === 'signup' || mode === 'forgot') && (
            <div>
              <label className="block text-sm font-medium text-muted mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-field text-ink border border-hairline rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent"
                autoComplete="email"
                autoFocus
                required
              />
            </div>
          )}

          {mode === 'signup' && (
            <div>
              <label className="block text-sm font-medium text-muted mb-1.5">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-field text-ink border border-hairline rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent"
                autoComplete="username"
                required
              />
            </div>
          )}

          {(mode === 'login' || mode === 'signup' || mode === 'reset') && (
            <div>
              <label className="block text-sm font-medium text-muted mb-1.5">
                {mode === 'reset' ? 'New password' : 'Password'}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-field text-ink border border-hairline rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                required
              />
            </div>
          )}

          {(mode === 'signup' || mode === 'reset') && (
            <div>
              <label className="block text-sm font-medium text-muted mb-1.5">Confirm password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-field text-ink border border-hairline rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent"
                autoComplete="new-password"
                required
              />
            </div>
          )}

          {notice && <p className="text-positive text-sm">{notice}</p>}
          {displayError && <p className="text-danger text-sm">{displayError}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full h-[52px] bg-brand text-white text-base font-semibold rounded-lg hover:bg-brand-hover transition disabled:opacity-50"
          >
            {submitLabels[mode]}
          </button>
        </form>

        <div className="mt-4 text-center text-sm text-muted space-y-1">
          {mode === 'login' && (
            <>
              <div>
                Don&apos;t have an account?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('signup')}
                  className="text-accent hover:underline font-medium"
                >
                  Sign up
                </button>
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => switchMode('forgot')}
                  className="text-accent hover:underline font-medium"
                >
                  Forgot password?
                </button>
              </div>
            </>
          )}
          {mode === 'signup' && (
            <div>
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="text-accent hover:underline font-medium"
              >
                Log in
              </button>
            </div>
          )}
          {(mode === 'forgot' || mode === 'reset') && (
            <div>
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="text-accent hover:underline font-medium"
              >
                Back to log in
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoginGate;
